import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import PublicShell from "@/components/PublicShell";
import { VideoSlot, useVideoPlacements } from "@/components/VideoSlot";
import { HeroVideo } from "@/components/HeroVideo";
import ArtCard from "@/components/ArtCard";
import { useCopy } from "@/context/ContentContext";
import { ChevronDown, ArrowRight } from "lucide-react";
import { FitImage } from "@/components/FitImage";

const DEFAULT_HERO = "https://images.unsplash.com/photo-1618331833071-ce81bd50d300?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMG9pbCUyMHBhaW50aW5nJTIwY2FudmFzfGVufDB8fHx8MTc4MzI5MjU0M3ww&ixlib=rb-4.1.0&q=70&w=1600&auto=format";
const DEFAULT_STUDIO = "https://images.unsplash.com/photo-1644375391877-0ae77eeed8fc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwzfHxhcnRpc3QlMjBzdHVkaW8lMjBwYWludGluZ3xlbnwwfHx8fDE3ODMyOTI1NDN8MA&ixlib=rb-4.1.0&q=70&w=1000&auto=format";

export default function HomePage() {
  const [recent, setRecent] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [images, setImages] = useState(null); // slot -> {url, focal}; null until loaded
  const [artistPhotos, setArtistPhotos] = useState([]);
  const heroTitle = useCopy("hero_title_full", "Art Born from Soul");
  const heroTag = useCopy("hero_tagline", "A space where the hearts' glow, the souls' whisper, and the worlds' hidden beauty gather and come to life on canvas through the artists hand.");
  const artistTitle = useCopy("artist_title", "The Artist");
  const artistBody = useCopy("artist_body", "Every piece is a conversation between medium and meaning—crafted by hand, informed by heart.");
  const recentTitle = useCopy("recent_title", "Recent Works");
  const envisionTitle = useCopy("envision_title", "Envision Something Unique?");
  const envisionBody = useCopy("envision_body", "Commission a custom piece tailored to your space, your palette, your story.");

  useEffect(() => {
    api.get("/artworks").then(r => setRecent(r.data.slice(0, 6)));
    api.get("/reviews").then(r => setReviews(r.data.slice(0, 3)));
    api.get("/site-images").then(r => {
      const map = {};
      (r.data.slots || []).forEach(s => { if (s.image_url) map[s.slot] = { url: fileUrl(s.image_url), focalX: s.focal_x, focalY: s.focal_y }; });
      setImages(map);
    }).catch(() => setImages({}));
    api.get("/artist-gallery").then(r => setArtistPhotos(r.data.photos || [])).catch(() => setArtistPhotos([]));
  }, []);

  const scrollToCollection = () => {
    document.getElementById("collection")?.scrollIntoView({ behavior: "smooth" });
  };
  const hero = images?.hero_bg;
  const studio = images?.studio_bg;
  const envision = images?.envision_bg;
  // Wait for the CMS lookup so we never download the default image only to swap it out.
  const heroImg = images ? (hero?.url || DEFAULT_HERO) : null;
  const studioImg = images ? (studio?.url || DEFAULT_STUDIO) : null;
  const envisionImg = envision?.url;
  const heroVideo = useVideoPlacements("home", "hero_background")[0];

  return (
    <PublicShell transparentNav>
      {/* HERO */}
      <section className="relative h-screen min-h-[640px] w-full overflow-hidden">
        {heroImg && <FitImage src={heroImg} focalX={hero?.focalX} focalY={hero?.focalY} priority sizes="100vw" className="absolute inset-0" data-testid="home-hero-img" />}
        {heroVideo && <HeroVideo placement={heroVideo} />}
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/10 to-black/70"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-black/60"></div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 h-full flex flex-col justify-end pb-24 md:pb-32">
          <h1 data-testid="home-hero-title" className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-none font-light text-[color:var(--brand-cream)] max-w-4xl reveal drop-shadow-lg">
            {heroTitle}
          </h1>
          <button
            onClick={scrollToCollection}
            data-testid="hero-explore"
            className="mt-10 inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.28em] text-[color:var(--brand-cream)] hover:text-accent transition-colors self-start reveal reveal-delay-1"
          >
            Explore the Collection
            <ChevronDown size={16} className="animate-bounce" />
          </button>
        </div>
      </section>

      <VideoSlot page="home" spot="below_hero" className="py-16 md:py-20" />

      {/* TAGLINE */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 py-24 md:py-32 text-center">
        <p data-testid="home-hero-tagline" className="font-serif text-2xl md:text-3xl lg:text-4xl leading-relaxed text-ink-soft font-light">
          {heroTag}
        </p>
      </section>

      {/* ARTIST */}
      <section className="bg-cream-soft py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-16 items-center">
          <div>
            <div className="overline text-mute mb-6">The Studio</div>
            <h2 data-testid="home-artist-title" className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-tight mb-8 text-ink">
              {artistTitle}
            </h2>
            <p className="font-serif text-xl md:text-2xl leading-relaxed text-ink-soft font-light">
              {artistBody}
            </p>
          </div>
          {studioImg && <FitImage src={studioImg} alt="Artist studio" focalX={studio?.focalX} focalY={studio?.focalY} sizes="(min-width: 1024px) 45vw, 100vw" className="relative aspect-[4/5] bg-cream-soft" data-testid="home-studio-img" />}
        </div>

        {/* Artist gallery (admin-managed) */}
        {artistPhotos.length > 0 && (
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 mt-16 md:mt-24" data-testid="artist-gallery-section">
            <div className="overline text-mute mb-6">In the Studio</div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {artistPhotos.map(p => (
                <figure key={p.id} className="group" data-testid={`artist-gallery-photo-${p.id}`}>
                  <FitImage src={fileUrl(p.image_url)} alt={p.caption || "Studio moment"} sizes="(min-width: 1024px) 25vw, (min-width: 640px) 33vw, 50vw" className="relative aspect-[4/5] bg-cream-soft" imgClassName="transition-transform duration-500 group-hover:scale-[1.03]" />
                  {p.caption && (
                    <figcaption className="text-[11px] uppercase tracking-[0.2em] text-mute mt-2">{p.caption}</figcaption>
                  )}
                </figure>
              ))}
            </div>
          </div>
        )}
      </section>

      <VideoSlot page="home" spot="above_collection" className="pb-8" />

      {/* RECENT WORKS */}
      <section id="collection" className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="flex items-end justify-between mb-14 flex-wrap gap-4">
          <div>
            <div className="overline text-mute mb-4">Collection</div>
            <h2 data-testid="home-recent-title" className="font-serif text-4xl md:text-5xl lg:text-6xl font-light text-ink">{recentTitle}</h2>
          </div>
          <Link to="/gallery" className="text-[11px] uppercase tracking-[0.22em] text-ink hover:text-accent inline-flex items-center gap-2" data-testid="home-view-all">
            View All <ArrowRight size={14} />
          </Link>
        </div>
        {recent.length === 0 ? (
          <p className="text-mute">Pieces coming soon.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
            {recent.map(a => <ArtCard key={a.id} art={a} />)}
          </div>
        )}
      </section>

      {/* ENVISION - Dark CTA */}
      <section className="relative bg-ink py-28 md:py-40 overflow-hidden">
        {envisionImg && (
          <>
            <FitImage src={envisionImg} focalX={envision?.focalX} focalY={envision?.focalY} className="absolute inset-0 opacity-40" data-testid="home-envision-img" />
            <div className="absolute inset-0 bg-ink/60"></div>
          </>
        )}
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <h2 data-testid="home-envision-title" className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-tight text-[color:var(--brand-cream)] mb-8">
            {envisionTitle}
          </h2>
          <p className="text-lg md:text-xl leading-relaxed text-[color:var(--brand-cream)]/70 mb-12 max-w-2xl mx-auto">
            {envisionBody}
          </p>
          <Link to="/custom" className="btn-outline-light" data-testid="envision-cta">
            Request a Commission
          </Link>
        </div>
      </section>

      {/* REVIEWS */}
      {reviews.length > 0 && (
        <section className="bg-cream-soft py-24 md:py-32">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12">
            <div className="overline text-mute mb-4 text-center">Client Words</div>
            <h2 className="font-serif text-4xl md:text-5xl font-light mb-16 text-center text-ink">In their own words</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {reviews.map(r => (
                <div key={r.id} className="bg-cream p-8 border border-ink-10">
                  <div className="text-accent text-lg mb-4">{"★".repeat(r.rating)}</div>
                  <p className="font-serif text-lg leading-relaxed text-ink-soft mb-6">&ldquo;{r.comment}&rdquo;</p>
                  <div className="overline text-mute">— {r.name}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
      <VideoSlot page="home" spot="above_footer" className="py-16 md:py-20" />
    </PublicShell>
  );
}
